import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCollection, requestJson } from '../../lib/api';

export default function Search() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      setResults([]);
      setOpen(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const payload = await requestJson(`/search?q=${encodeURIComponent(normalizedQuery)}`);
        setResults(getCollection(payload));
        setOpen(true);
      } catch (err) {
        setError(err.message);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 220);

    return () => window.clearTimeout(timeoutId);
  }, [query]);

  const handleOpenResult = (result) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    navigate(result.path);
  };

  return (
    <FormControl sx={{ width: '100%' }} variant="outlined">
      <OutlinedInput
        inputRef={inputRef}
        size="medium"
        id="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => query.trim().length >= 2 && setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && results[0]) {
            event.preventDefault();
            handleOpenResult(results[0]);
          } else if (event.key === 'Enter' || event.key === 'Escape') {
            setOpen(false);
          }
        }}
        placeholder="Rechercher logistique, suivis, clients, sites, stock…"
        sx={{
          borderRadius: 2,
          height: 46,
          bgcolor: 'background.default',
          boxShadow: 'inset 0 0 0 1px rgba(102,178,255,0.10)',
        }}
        startAdornment={
          <InputAdornment position="start" sx={{ color: query ? 'primary.main' : 'text.secondary' }}>
            <SearchRoundedIcon fontSize="small" />
          </InputAdornment>
        }
        inputProps={{ 'aria-label': 'search' }}
      />
      <Popper
        open={open}
        anchorEl={inputRef.current}
        placement="bottom-start"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={8}
            sx={{
              width: inputRef.current?.offsetWidth || 420,
              mt: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ p: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.75 }}>
                <SearchRoundedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Recherche globale
                </Typography>
                {loading && <Chip size="small" label="Recherche…" />}
              </Stack>
              {error ? (
                <Alert severity="error">{error}</Alert>
              ) : results.length ? (
                <List dense disablePadding sx={{ maxHeight: 420, overflow: 'auto' }}>
                  {results.map((result) => (
                    <ListItemButton key={result.id} onClick={() => handleOpenResult(result)} sx={{ borderRadius: 1 }}>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={result.type} color="primary" variant="outlined" />
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              {result.title}
                            </Typography>
                          </Stack>
                        }
                        secondary={result.subtitle}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>
                  Aucun résultat.
                </Box>
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </FormControl>
  );
}
